import { Injectable } from "@angular/core";
import * as XLSX from "xlsx";
import {
  ExcludedPropertyExportRecord,
  ExcludedPropertyImportRecord,
  ImportEntityType,
  ResourceExportRecord,
  ResourceFullExportResponse,
  ResourceFullImportData,
  ResourceImportRecord,
  ResourceSkillExportRecord,
  ResourceSkillImportRecord,
  ResourceZoneExportRecord,
  ResourceZoneImportRecord,
  RosterAssignmentExportRecord,
  RosterAssignmentImportRecord,
  SorCodeExportRecord,
  SorCodeFullImportData,
  SorCodeImportRecord,
  SorCodeOverrideExportRecord,
  SorCodeOverrideImportRecord
} from "@app/models/dto/data-import.model";

/**
 * Service for client-side Excel file parsing and generation.
 * Uses SheetJS (xlsx) library for Excel operations.
 * CR: ROCC-CR008745
 */
@Injectable({
  providedIn: "root"
})
export class ExcelService {
  // Column definitions for Resources
  private readonly resourceColumns = [
    { key: "id", header: "ID", width: 10 },
    { key: "name", header: "Name", width: 30 },
    { key: "team", header: "Team", width: 20 },
    { key: "location", header: "Location (Postcode)", width: 20 },
    { key: "workMode", header: "Work Mode", width: 15 },
    { key: "cost", header: "Cost", width: 10 }
  ];

  // Column definitions for Resource Skills (with Delete flag)
  private readonly resourceSkillColumns = [
    { key: "id", header: "ID", width: 10 },
    { key: "resourceName", header: "Resource Name", width: 30 },
    { key: "skill", header: "Skill", width: 25 },
    { key: "startDate", header: "Start Date", width: 15 },
    { key: "endDate", header: "End Date", width: 15 },
    { key: "mappingPattern", header: "Mapping Pattern", width: 25 },
    { key: "delete", header: "Delete", width: 10 }
  ];

  // Column definitions for Resource Zones (with Delete flag)
  private readonly resourceZoneColumns = [
    { key: "id", header: "ID", width: 10 },
    { key: "resourceName", header: "Resource Name", width: 30 },
    { key: "zone", header: "Zone", width: 25 },
    { key: "startDate", header: "Start Date", width: 15 },
    { key: "endDate", header: "End Date", width: 15 },
    { key: "mappingPattern", header: "Mapping Pattern", width: 25 },
    { key: "delete", header: "Delete", width: 10 }
  ];

  // Column definitions for Roster Assignments (with Delete flag)
  private readonly rosterAssignmentColumns = [
    { key: "id", header: "ID", width: 10 },
    { key: "resourceName", header: "Resource Name", width: 30 },
    { key: "division", header: "Division", width: 25 },
    { key: "startDate", header: "Start Date", width: 15 },
    { key: "endDate", header: "End Date", width: 15 },
    { key: "delete", header: "Delete", width: 10 }
  ];

  // Column definitions for Excluded Properties (with Delete flag)
  private readonly excludedPropertyColumns = [
    { key: "id", header: "ID", width: 10 },
    { key: "resourceName", header: "Resource Name", width: 30 },
    { key: "propertyReference", header: "Property Reference", width: 40 },
    { key: "delete", header: "Delete", width: 10 }
  ];

  // Column definitions for SOR Codes
  private readonly sorCodeColumns = [
    { key: "id", header: "ID", width: 10 },
    { key: "code", header: "Code", width: 20 },
    { key: "description", header: "Description", width: 50 },
    { key: "smv", header: "SMV", width: 10 },
    { key: "visitType", header: "Visit Type", width: 30 }
  ];

  // Column definitions for SOR Code Overrides
  private readonly sorCodeOverrideColumns = [
    { key: "id", header: "ID", width: 10 },
    { key: "sorCode", header: "SOR Code", width: 20 },
    { key: "contracts", header: "Contracts", width: 30 },
    { key: "workTypes", header: "Work Types", width: 30 },
    { key: "visitType", header: "Visit Type", width: 30 },
    { key: "delete", header: "Delete", width: 10 }
  ];

  constructor() {}

  /**
   * Parse an Excel file and extract records based on entity type.
   * For RESOURCE type, only returns the main resource records (use parseResourceExcelFull for all sheets).
   */
  async parseExcelFile(
    file: File,
    entityType: ImportEntityType
  ): Promise<ResourceImportRecord[] | SorCodeImportRecord[]> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (e: ProgressEvent<FileReader>) => {
        try {
          const data = new Uint8Array(e.target?.result as ArrayBuffer);
          const workbook = XLSX.read(data, { type: "array" });

          // Get the first sheet
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];

          // Convert to JSON with header mapping
          const jsonData = XLSX.utils.sheet_to_json(worksheet, {
            defval: null
          });

          if (entityType === "RESOURCE") {
            resolve(this.mapToResourceRecords(jsonData));
          } else {
            resolve(this.mapToSorCodeRecords(jsonData));
          }
        } catch (error) {
          reject(new Error(`Failed to parse Excel file: ${error}`));
        }
      };

      reader.onerror = () => {
        reject(new Error("Failed to read file"));
      };

      reader.readAsArrayBuffer(file);
    });
  }

  /**
   * Parse a full Resource Excel file with all sheets (Resources, Skills, Zones, Roster, Excluded Properties).
   * Returns comprehensive import data including child records.
   */
  async parseResourceExcelFull(file: File): Promise<ResourceFullImportData> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (e: ProgressEvent<FileReader>) => {
        try {
          const data = new Uint8Array(e.target?.result as ArrayBuffer);
          const workbook = XLSX.read(data, { type: "array" });

          const result: ResourceFullImportData = {
            resources: [],
            skills: [],
            zones: [],
            rosterAssignments: [],
            excludedProperties: []
          };

          // Parse each sheet by name (case-insensitive match)
          for (const sheetName of workbook.SheetNames) {
            const worksheet = workbook.Sheets[sheetName];
            const jsonData = XLSX.utils.sheet_to_json(worksheet, {
              defval: null
            });

            const lowerName = sheetName.toLowerCase();

            if (
              lowerName === "resources" ||
              (lowerName.startsWith("resource") &&
                !lowerName.includes("skill") &&
                !lowerName.includes("zone"))
            ) {
              result.resources = this.mapToResourceRecords(jsonData);
            } else if (lowerName.includes("skill")) {
              result.skills = this.mapToSkillImportRecords(jsonData);
            } else if (lowerName.includes("zone")) {
              result.zones = this.mapToZoneImportRecords(jsonData);
            } else if (
              lowerName.includes("roster") ||
              lowerName.includes("assignment")
            ) {
              result.rosterAssignments =
                this.mapToRosterAssignmentImportRecords(jsonData);
            } else if (
              lowerName.includes("excluded") ||
              lowerName.includes("propert")
            ) {
              result.excludedProperties =
                this.mapToExcludedPropertyImportRecords(jsonData);
            }
          }

          resolve(result);
        } catch (error) {
          reject(new Error(`Failed to parse Excel file: ${error}`));
        }
      };

      reader.onerror = () => {
        reject(new Error("Failed to read file"));
      };

      reader.readAsArrayBuffer(file);
    });
  }

  /**
   * Parse a SOR Code Excel file with optional Overrides sheet.
   * Returns both SOR code records and override records.
   */
  async parseSorCodeExcelFull(file: File): Promise<SorCodeFullImportData> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (e: ProgressEvent<FileReader>) => {
        try {
          const data = new Uint8Array(e.target?.result as ArrayBuffer);
          const workbook = XLSX.read(data, { type: "array" });

          const result: SorCodeFullImportData = {
            records: [],
            overrides: []
          };

          for (const sheetName of workbook.SheetNames) {
            const worksheet = workbook.Sheets[sheetName];
            const jsonData = XLSX.utils.sheet_to_json(worksheet, {
              defval: null
            });

            const lowerName = sheetName.toLowerCase();

            if (lowerName.includes("override")) {
              result.overrides = this.mapToSorCodeOverrideRecords(jsonData);
            } else if (
              lowerName.includes("sor") ||
              lowerName === workbook.SheetNames[0].toLowerCase()
            ) {
              // First sheet or any sheet with "sor" in the name (but not "override")
              if (
                !lowerName.includes("reference") &&
                !lowerName.includes("instruction")
              ) {
                result.records = this.mapToSorCodeRecords(jsonData);
              }
            }
          }

          resolve(result);
        } catch (error) {
          reject(new Error(`Failed to parse Excel file: ${error}`));
        }
      };

      reader.onerror = () => {
        reject(new Error("Failed to read file"));
      };

      reader.readAsArrayBuffer(file);
    });
  }

  /**
   * Generate an Excel file for Resources export
   */
  generateResourceExcel(
    records: ResourceExportRecord[],
    referenceData?: { teams: string[]; locations: string[] }
  ): void {
    const workbook = XLSX.utils.book_new();

    // Main data sheet
    const headers = this.resourceColumns.map((col) => col.header);
    const dataRows = records.map((record) => [
      record.id,
      record.name,
      record.team,
      record.location,
      record.workMode,
      record.cost
    ]);

    const worksheet = XLSX.utils.aoa_to_sheet([headers, ...dataRows]);
    this.setColumnWidths(worksheet, this.resourceColumns);
    this.addAutoFilter(worksheet, dataRows.length, this.resourceColumns.length);
    XLSX.utils.book_append_sheet(workbook, worksheet, "Resources");

    // Reference data sheet (optional)
    if (referenceData) {
      const refSheet = this.createReferenceSheet(referenceData);
      XLSX.utils.book_append_sheet(workbook, refSheet, "Reference Data");
    }

    // Instructions sheet
    const instructionsSheet = this.createResourceInstructionsSheet();
    XLSX.utils.book_append_sheet(workbook, instructionsSheet, "Instructions");

    // Download
    XLSX.writeFile(workbook, "resources_export.xlsx");
  }

  /**
   * Generate an empty Resources template
   */
  generateResourceTemplate(referenceData?: {
    teams: string[];
    skills: string[];
    zones: string[];
    divisions: string[];
    mappingPatterns: string[];
  }): void {
    const workbook = XLSX.utils.book_new();

    // Empty data sheet with headers
    const headers = this.resourceColumns.map((col) => col.header);
    const worksheet = XLSX.utils.aoa_to_sheet([headers]);
    this.setColumnWidths(worksheet, this.resourceColumns);
    this.addAutoFilter(worksheet, 0, this.resourceColumns.length);
    XLSX.utils.book_append_sheet(workbook, worksheet, "Resources");

    // Empty Skills sheet
    const skillHeaders = this.resourceSkillColumns.map((col) => col.header);
    const skillSheet = XLSX.utils.aoa_to_sheet([skillHeaders]);
    this.setColumnWidths(skillSheet, this.resourceSkillColumns);
    this.addAutoFilter(skillSheet, 0, this.resourceSkillColumns.length);
    XLSX.utils.book_append_sheet(workbook, skillSheet, "Skills");

    // Empty Zones sheet
    const zoneHeaders = this.resourceZoneColumns.map((col) => col.header);
    const zoneSheet = XLSX.utils.aoa_to_sheet([zoneHeaders]);
    this.setColumnWidths(zoneSheet, this.resourceZoneColumns);
    this.addAutoFilter(zoneSheet, 0, this.resourceZoneColumns.length);
    XLSX.utils.book_append_sheet(workbook, zoneSheet, "Zones");

    // Empty Roster Assignments sheet
    const rosterHeaders = this.rosterAssignmentColumns.map((col) => col.header);
    const rosterSheet = XLSX.utils.aoa_to_sheet([rosterHeaders]);
    this.setColumnWidths(rosterSheet, this.rosterAssignmentColumns);
    this.addAutoFilter(rosterSheet, 0, this.rosterAssignmentColumns.length);
    XLSX.utils.book_append_sheet(workbook, rosterSheet, "Roster Assignments");

    // Empty Excluded Properties sheet
    const excludedHeaders = this.excludedPropertyColumns.map(
      (col) => col.header
    );
    const excludedSheet = XLSX.utils.aoa_to_sheet([excludedHeaders]);
    this.setColumnWidths(excludedSheet, this.excludedPropertyColumns);
    this.addAutoFilter(excludedSheet, 0, this.excludedPropertyColumns.length);
    XLSX.utils.book_append_sheet(
      workbook,
      excludedSheet,
      "Excluded Properties"
    );

    // Reference data sheet
    if (referenceData) {
      const refSheet = this.createFullReferenceSheet(referenceData);
      XLSX.utils.book_append_sheet(workbook, refSheet, "Reference Data");
    }

    // Instructions sheet
    const instructionsSheet = this.createResourceInstructionsSheet();
    XLSX.utils.book_append_sheet(workbook, instructionsSheet, "Instructions");

    XLSX.writeFile(workbook, "resources_template.xlsx");
  }

  /**
   * Generate a full resource export with all related data in separate sheets.
   * Includes: Resources, Skills, Zones, Roster Assignments, Excluded Properties
   * Each data sheet is formatted as an Excel table for better usability.
   */
  generateResourceFullExcel(exportData: ResourceFullExportResponse): void {
    const workbook = XLSX.utils.book_new();

    // Main Resources sheet
    const resourceHeaders = this.resourceColumns.map((col) => col.header);
    const resourceRows = exportData.resources.map((record) => [
      record.id,
      record.name,
      record.team,
      record.location,
      record.workMode,
      record.cost
    ]);
    const resourceSheet = XLSX.utils.aoa_to_sheet([
      resourceHeaders,
      ...resourceRows
    ]);
    this.setColumnWidths(resourceSheet, this.resourceColumns);
    this.addAutoFilter(
      resourceSheet,
      resourceRows.length,
      this.resourceColumns.length
    );
    XLSX.utils.book_append_sheet(workbook, resourceSheet, "Resources");

    // Skills sheet
    const skillHeaders = this.resourceSkillColumns.map((col) => col.header);
    const skillRows = exportData.skills.map((record) => [
      record.id,
      record.resourceName,
      record.skill,
      this.formatDate(record.startDate),
      this.formatDate(record.endDate),
      record.mappingPattern,
      "" // Delete column empty for exports
    ]);
    const skillSheet = XLSX.utils.aoa_to_sheet([skillHeaders, ...skillRows]);
    this.setColumnWidths(skillSheet, this.resourceSkillColumns);
    this.addAutoFilter(
      skillSheet,
      skillRows.length,
      this.resourceSkillColumns.length
    );
    XLSX.utils.book_append_sheet(workbook, skillSheet, "Skills");

    // Zones sheet
    const zoneHeaders = this.resourceZoneColumns.map((col) => col.header);
    const zoneRows = exportData.zones.map((record) => [
      record.id,
      record.resourceName,
      record.zone,
      this.formatDate(record.startDate),
      this.formatDate(record.endDate),
      record.mappingPattern,
      "" // Delete column empty for exports
    ]);
    const zoneSheet = XLSX.utils.aoa_to_sheet([zoneHeaders, ...zoneRows]);
    this.setColumnWidths(zoneSheet, this.resourceZoneColumns);
    this.addAutoFilter(
      zoneSheet,
      zoneRows.length,
      this.resourceZoneColumns.length
    );
    XLSX.utils.book_append_sheet(workbook, zoneSheet, "Zones");

    // Roster Assignments sheet
    const rosterHeaders = this.rosterAssignmentColumns.map((col) => col.header);
    const rosterRows = exportData.rosterAssignments.map((record) => [
      record.id,
      record.resourceName,
      record.division,
      this.formatDate(record.startDate),
      this.formatDate(record.endDate),
      "" // Delete column empty for exports
    ]);
    const rosterSheet = XLSX.utils.aoa_to_sheet([rosterHeaders, ...rosterRows]);
    this.setColumnWidths(rosterSheet, this.rosterAssignmentColumns);
    this.addAutoFilter(
      rosterSheet,
      rosterRows.length,
      this.rosterAssignmentColumns.length
    );
    XLSX.utils.book_append_sheet(workbook, rosterSheet, "Roster Assignments");

    // Excluded Properties sheet
    const excludedHeaders = this.excludedPropertyColumns.map(
      (col) => col.header
    );
    const excludedRows = exportData.excludedProperties.map((record) => [
      record.id,
      record.resourceName,
      record.propertyReference,
      "" // Delete column empty for exports
    ]);
    const excludedSheet = XLSX.utils.aoa_to_sheet([
      excludedHeaders,
      ...excludedRows
    ]);
    this.setColumnWidths(excludedSheet, this.excludedPropertyColumns);
    this.addAutoFilter(
      excludedSheet,
      excludedRows.length,
      this.excludedPropertyColumns.length
    );
    XLSX.utils.book_append_sheet(
      workbook,
      excludedSheet,
      "Excluded Properties"
    );

    // Reference data sheet
    const refSheet = this.createFullReferenceSheet({
      teams: exportData.validTeams,
      skills: exportData.validSkills,
      zones: exportData.validZones,
      divisions: exportData.validDivisions,
      mappingPatterns: exportData.validMappingPatterns
    });
    XLSX.utils.book_append_sheet(workbook, refSheet, "Reference Data");

    // Instructions sheet
    const instructionsSheet = this.createResourceInstructionsSheet();
    XLSX.utils.book_append_sheet(workbook, instructionsSheet, "Instructions");

    XLSX.writeFile(workbook, "resources_export.xlsx");
  }

  /**
   * Generate an Excel file for SOR Codes export
   */
  generateSorCodeExcel(
    records: SorCodeExportRecord[],
    referenceData?: { visitTypes: string[] },
    overrideRecords?: SorCodeOverrideExportRecord[]
  ): void {
    const workbook = XLSX.utils.book_new();

    // Main data sheet
    const headers = this.sorCodeColumns.map((col) => col.header);
    const dataRows = records.map((record) => [
      record.id,
      record.code,
      record.description,
      record.smv,
      record.visitType
    ]);

    const worksheet = XLSX.utils.aoa_to_sheet([headers, ...dataRows]);
    this.setColumnWidths(worksheet, this.sorCodeColumns);
    this.addAutoFilter(worksheet, dataRows.length, this.sorCodeColumns.length);
    XLSX.utils.book_append_sheet(workbook, worksheet, "SOR Codes");

    // SOR Code Overrides sheet
    if (overrideRecords && overrideRecords.length > 0) {
      const overrideSheet = this.createSorCodeOverrideSheet(overrideRecords);
      XLSX.utils.book_append_sheet(
        workbook,
        overrideSheet,
        "SOR Code Overrides"
      );
    } else {
      // Empty overrides sheet with headers
      const overrideHeaders = this.sorCodeOverrideColumns.map(
        (col) => col.header
      );
      const emptyOverrideSheet = XLSX.utils.aoa_to_sheet([overrideHeaders]);
      this.setColumnWidths(emptyOverrideSheet, this.sorCodeOverrideColumns);
      XLSX.utils.book_append_sheet(
        workbook,
        emptyOverrideSheet,
        "SOR Code Overrides"
      );
    }

    // Reference data sheet (optional)
    if (referenceData) {
      const refSheet = this.createSorReferenceSheet(referenceData);
      XLSX.utils.book_append_sheet(workbook, refSheet, "Reference Data");
    }

    // Instructions sheet
    const instructionsSheet = this.createSorCodeInstructionsSheet();
    XLSX.utils.book_append_sheet(workbook, instructionsSheet, "Instructions");

    XLSX.writeFile(workbook, "sor_codes_export.xlsx");
  }

  /**
   * Generate an empty SOR Codes template
   */
  generateSorCodeTemplate(referenceData?: { visitTypes: string[] }): void {
    const workbook = XLSX.utils.book_new();

    // Empty data sheet with headers
    const headers = this.sorCodeColumns.map((col) => col.header);
    const worksheet = XLSX.utils.aoa_to_sheet([headers]);
    this.setColumnWidths(worksheet, this.sorCodeColumns);
    this.addAutoFilter(worksheet, 0, this.sorCodeColumns.length);
    XLSX.utils.book_append_sheet(workbook, worksheet, "SOR Codes");

    // Empty overrides sheet with headers
    const overrideHeaders = this.sorCodeOverrideColumns.map(
      (col) => col.header
    );
    const overrideSheet = XLSX.utils.aoa_to_sheet([overrideHeaders]);
    this.setColumnWidths(overrideSheet, this.sorCodeOverrideColumns);
    this.addAutoFilter(overrideSheet, 0, this.sorCodeOverrideColumns.length);
    XLSX.utils.book_append_sheet(workbook, overrideSheet, "SOR Code Overrides");

    // Reference data sheet
    if (referenceData) {
      const refSheet = this.createSorReferenceSheet(referenceData);
      XLSX.utils.book_append_sheet(workbook, refSheet, "Reference Data");
    }

    // Instructions sheet
    const instructionsSheet = this.createSorCodeInstructionsSheet();
    XLSX.utils.book_append_sheet(workbook, instructionsSheet, "Instructions");

    XLSX.writeFile(workbook, "sor_codes_template.xlsx");
  }

  /**
   * Validate file type is xlsx
   */
  isValidExcelFile(file: File): boolean {
    const validTypes = [
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "application/vnd.ms-excel"
    ];
    return (
      validTypes.includes(file.type) ||
      file.name.toLowerCase().endsWith(".xlsx") ||
      file.name.toLowerCase().endsWith(".xls")
    );
  }

  // ============================================
  // PRIVATE METHODS
  // ============================================

  private mapToResourceRecords(jsonData: unknown[]): ResourceImportRecord[] {
    return jsonData.map((row: Record<string, unknown>) => ({
      id: this.parseId(row["ID"]),
      name: this.parseString(row["Name"]),
      team: this.parseString(row["Team"]),
      location: this.parseString(row["Location (Postcode)"]),
      workMode: this.parseWorkMode(row["Work Mode"]),
      cost: this.parseNumber(row["Cost"])
    }));
  }

  private mapToSorCodeRecords(jsonData: unknown[]): SorCodeImportRecord[] {
    return jsonData.map((row: Record<string, unknown>) => ({
      id: this.parseId(row["ID"]),
      code: this.parseString(row["Code"]),
      description: this.parseString(row["Description"]),
      smv: this.parseNumber(row["SMV"]),
      visitType: this.parseString(row["Visit Type"])
    }));
  }

  private mapToSorCodeOverrideRecords(
    jsonData: unknown[]
  ): SorCodeOverrideImportRecord[] {
    return jsonData.map((row: Record<string, unknown>) => ({
      id: this.parseId(row["ID"]),
      sorCode: this.parseString(row["SOR Code"]),
      contracts: this.parseString(row["Contracts"]),
      workTypes: this.parseString(row["Work Types"]),
      visitType: this.parseString(row["Visit Type"]),
      delete: this.parseDeleteFlag(row["Delete"])
    }));
  }

  private createSorCodeOverrideSheet(
    records: SorCodeOverrideExportRecord[]
  ): XLSX.WorkSheet {
    const headers = this.sorCodeOverrideColumns.map((col) => col.header);
    const dataRows = records.map((record) => [
      record.id,
      record.sorCode,
      record.contracts,
      record.workTypes,
      record.visitType,
      record.delete
    ]);

    const worksheet = XLSX.utils.aoa_to_sheet([headers, ...dataRows]);
    this.setColumnWidths(worksheet, this.sorCodeOverrideColumns);
    this.addAutoFilter(
      worksheet,
      dataRows.length,
      this.sorCodeOverrideColumns.length
    );
    return worksheet;
  }

  private mapToSkillImportRecords(
    jsonData: unknown[]
  ): ResourceSkillImportRecord[] {
    return jsonData.map((row: Record<string, unknown>) => ({
      id: this.parseId(row["ID"]),
      resourceName: this.parseString(row["Resource Name"]),
      skill: this.parseString(row["Skill"]),
      startDate: this.parseString(row["Start Date"]),
      endDate: this.parseString(row["End Date"]),
      mappingPattern: this.parseString(row["Mapping Pattern"]),
      delete: this.parseDeleteFlag(row["Delete"])
    }));
  }

  private mapToZoneImportRecords(
    jsonData: unknown[]
  ): ResourceZoneImportRecord[] {
    return jsonData.map((row: Record<string, unknown>) => ({
      id: this.parseId(row["ID"]),
      resourceName: this.parseString(row["Resource Name"]),
      zone: this.parseString(row["Zone"]),
      startDate: this.parseString(row["Start Date"]),
      endDate: this.parseString(row["End Date"]),
      mappingPattern: this.parseString(row["Mapping Pattern"]),
      delete: this.parseDeleteFlag(row["Delete"])
    }));
  }

  private mapToRosterAssignmentImportRecords(
    jsonData: unknown[]
  ): RosterAssignmentImportRecord[] {
    return jsonData.map((row: Record<string, unknown>) => ({
      id: this.parseId(row["ID"]),
      resourceName: this.parseString(row["Resource Name"]),
      division: this.parseString(row["Division"]),
      startDate: this.parseString(row["Start Date"]),
      endDate: this.parseString(row["End Date"]),
      delete: this.parseDeleteFlag(row["Delete"])
    }));
  }

  private mapToExcludedPropertyImportRecords(
    jsonData: unknown[]
  ): ExcludedPropertyImportRecord[] {
    return jsonData.map((row: Record<string, unknown>) => ({
      id: this.parseId(row["ID"]),
      resourceName: this.parseString(row["Resource Name"]),
      propertyReference: this.parseString(row["Property Reference"]),
      delete: this.parseDeleteFlag(row["Delete"])
    }));
  }

  private parseDeleteFlag(value: unknown): boolean {
    if (value === null || value === undefined || value === "") {
      return false;
    }
    const str = String(value).toLowerCase().trim();
    return str === "yes" || str === "true" || str === "1" || str === "y";
  }

  private parseId(value: unknown): number | undefined {
    if (value === null || value === undefined || value === "") {
      return undefined;
    }
    const num = Number(value);
    return isNaN(num) ? undefined : num;
  }

  private parseString(value: unknown): string {
    if (value === null || value === undefined) {
      return "";
    }
    return String(value).trim();
  }

  private parseNumber(value: unknown): number | undefined {
    if (value === null || value === undefined || value === "") {
      return undefined;
    }
    const num = Number(value);
    return isNaN(num) ? undefined : num;
  }

  private parseWorkMode(value: unknown): "Online" | "Offline" | undefined {
    const str = this.parseString(value).toLowerCase();
    if (str === "online") return "Online";
    if (str === "offline") return "Offline";
    return undefined;
  }

  private setColumnWidths(
    worksheet: XLSX.WorkSheet,
    columns: { width: number }[]
  ): void {
    worksheet["!cols"] = columns.map((col) => ({ wch: col.width }));
  }

  /**
   * Enable Excel's auto-filter on the header row.
   * This gives users sorting/filtering capability without requiring Pro tables.
   * Also works correctly when users later convert the range to a table.
   */
  private addAutoFilter(
    worksheet: XLSX.WorkSheet,
    rowCount: number,
    columnCount: number
  ): void {
    if (rowCount < 0) rowCount = 0;

    // Define the filter range (e.g., A1:F10)
    const endColumn = this.columnLetter(columnCount - 1);
    const endRow = rowCount + 1; // +1 for header row

    worksheet["!autofilter"] = {
      ref: `A1:${endColumn}${endRow}`
    };
  }

  /**
   * Convert a 0-based column index to an Excel column letter (A, B, ..., Z, AA, AB, ...)
   */
  private columnLetter(index: number): string {
    let letter = "";
    while (index >= 0) {
      letter = String.fromCharCode((index % 26) + 65) + letter;
      index = Math.floor(index / 26) - 1;
    }
    return letter;
  }

  private formatDate(dateStr: string | null): string {
    if (!dateStr) return "";
    // Parse ISO date and format as YYYY-MM-DD
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return dateStr;
      return date.toISOString().split("T")[0];
    } catch {
      return dateStr;
    }
  }

  private createReferenceSheet(data: {
    teams: string[];
    locations: string[];
  }): XLSX.WorkSheet {
    const maxRows = Math.max(data.teams.length, data.locations.length);
    const rows: string[][] = [["Valid Teams"]];

    for (let i = 0; i < maxRows; i++) {
      rows.push([data.teams[i] || ""]);
    }

    const worksheet = XLSX.utils.aoa_to_sheet(rows);
    worksheet["!cols"] = [{ wch: 30 }];
    return worksheet;
  }

  private createFullReferenceSheet(data: {
    teams: string[];
    skills: string[];
    zones: string[];
    divisions: string[];
    mappingPatterns: string[];
  }): XLSX.WorkSheet {
    const maxRows = Math.max(
      data.teams.length,
      data.skills.length,
      data.zones.length,
      data.divisions.length,
      data.mappingPatterns.length
    );
    const rows: string[][] = [
      [
        "Valid Teams",
        "Valid Skills",
        "Valid Zones",
        "Valid Divisions",
        "Valid Mapping Patterns"
      ]
    ];

    for (let i = 0; i < maxRows; i++) {
      rows.push([
        data.teams[i] || "",
        data.skills[i] || "",
        data.zones[i] || "",
        data.divisions[i] || "",
        data.mappingPatterns[i] || ""
      ]);
    }

    const worksheet = XLSX.utils.aoa_to_sheet(rows);
    worksheet["!cols"] = [
      { wch: 25 },
      { wch: 25 },
      { wch: 25 },
      { wch: 25 },
      { wch: 25 }
    ];
    return worksheet;
  }

  private createSorReferenceSheet(data: {
    visitTypes: string[];
  }): XLSX.WorkSheet {
    const rows: string[][] = [["Valid Visit Types"]];
    data.visitTypes.forEach((vt) => rows.push([vt]));

    const worksheet = XLSX.utils.aoa_to_sheet(rows);
    worksheet["!cols"] = [{ wch: 40 }];
    return worksheet;
  }

  private createResourceInstructionsSheet(): XLSX.WorkSheet {
    const instructions = [
      ["ROCC Resolve - Resource Import Instructions"],
      [""],
      ["=== Resources Sheet ==="],
      [
        "ID",
        "Leave empty for new resources. For existing resources, include the ID to update."
      ],
      [
        "Name",
        "Required. The name of the resource. Must be unique - duplicate names will be flagged."
      ],
      ["Team", "Required. Must match an existing team name exactly."],
      [
        "Location (Postcode)",
        "Required. Any valid UK postcode for the resource's base location."
      ],
      ["Work Mode", "Optional. Values: 'Online' or 'Offline'. Default: Online"],
      ["Cost", "Optional. Numeric value. Default: 1"],
      [""],
      ["=== Skills Sheet ==="],
      [
        "ID",
        "Leave BLANK for new rows. DO NOT CHANGE for existing rows (used to identify the record)."
      ],
      ["Resource Name", "Must match a resource name in the Resources sheet."],
      ["Skill", "Must match an existing skill name exactly."],
      ["Start Date", "Optional. Format: YYYY-MM-DD. When the skill starts."],
      ["End Date", "Optional. Format: YYYY-MM-DD. When the skill ends."],
      [
        "Mapping Pattern",
        "Optional. Must match an existing mapping pattern name."
      ],
      [
        "Delete",
        "Set to 'Yes' to remove this skill from the resource. Leave empty to add/update."
      ],
      [""],
      ["=== Zones Sheet ==="],
      [
        "ID",
        "Leave BLANK for new rows. DO NOT CHANGE for existing rows (used to identify the record)."
      ],
      ["Resource Name", "Must match a resource name in the Resources sheet."],
      ["Zone", "Must match an existing zone name exactly."],
      ["Start Date", "Optional. Format: YYYY-MM-DD. When the zone starts."],
      ["End Date", "Optional. Format: YYYY-MM-DD. When the zone ends."],
      [
        "Mapping Pattern",
        "Optional. Must match an existing mapping pattern name."
      ],
      [
        "Delete",
        "Set to 'Yes' to remove this zone from the resource. Leave empty to add/update."
      ],
      [""],
      ["=== Roster Assignments Sheet ==="],
      [
        "ID",
        "Leave BLANK for new rows. DO NOT CHANGE for existing rows (used to identify the record)."
      ],
      ["Resource Name", "Must match a resource name in the Resources sheet."],
      ["Division", "Must match an existing division name exactly."],
      [
        "Start Date",
        "Optional. Format: YYYY-MM-DD. When the assignment starts."
      ],
      ["End Date", "Optional. Format: YYYY-MM-DD. When the assignment ends."],
      [
        "Delete",
        "Set to 'Yes' to remove this assignment. Leave empty to add/update."
      ],
      [""],
      ["=== Excluded Properties Sheet ==="],
      [
        "ID",
        "Leave BLANK for new rows. DO NOT CHANGE for existing rows (used to identify the record)."
      ],
      ["Resource Name", "Must match a resource name in the Resources sheet."],
      ["Property Reference", "The property reference to exclude."],
      ["Delete", "Set to 'Yes' to remove this exclusion. Leave empty to add."],
      [""],
      ["=== Important Notes ==="],
      [
        "• ID Column: For existing records exported from the system, the ID identifies the exact",
        "database record. DO NOT modify or copy IDs between rows."
      ],
      [
        "• New Records: Leave the ID column BLANK - the system will assign an ID automatically."
      ],
      [
        "• Deleting Records: Set the Delete column to 'Yes' on exported rows to remove them."
      ],
      [
        "• Resource names must be unique.",
        "Duplicate names in the import will be flagged as errors."
      ],
      [
        "• The entire import will be rejected if ANY row has validation errors."
      ],
      ["• Check the 'Reference Data' sheet for valid lookup values."],
      ["• It is recommended to perform imports outside core business hours."],
      ["• Back up your data before performing large imports."]
    ];

    const worksheet = XLSX.utils.aoa_to_sheet(instructions);
    worksheet["!cols"] = [{ wch: 25 }, { wch: 70 }];
    return worksheet;
  }

  private createSorCodeInstructionsSheet(): XLSX.WorkSheet {
    const instructions = [
      ["ROCC Resolve - SOR Code Import Instructions"],
      [""],
      ["=== SOR Codes Sheet ==="],
      [
        "ID",
        "Leave empty for new SOR codes. For existing codes, include the ID to update."
      ],
      ["Code", "Required. Unique identifier for the SOR code."],
      ["Description", "Required. Description of the schedule of rates item."],
      ["SMV", "Optional. Standard Minute Value - numeric."],
      [
        "Visit Type",
        "Required. Must match an existing visit type description exactly."
      ],
      [""],
      ["=== SOR Code Overrides Sheet ==="],
      [
        "ID",
        "Leave empty for new overrides. For existing overrides, include the ID to update."
      ],
      ["SOR Code", "Required. Must match an existing SOR code."],
      [
        "Contracts",
        "Comma-separated contract references. At least one of Contracts or Work Types is required."
      ],
      [
        "Work Types",
        "Comma-separated work type references. At least one of Contracts or Work Types is required."
      ],
      [
        "Visit Type",
        "Required. The override visit type. Must match an existing visit type description exactly."
      ],
      [
        "Delete",
        "Set to 'Yes' to remove this override. Leave empty to add/update."
      ],
      [""],
      ["Important Notes:"],
      [
        "• The entire import will be rejected if ANY row has validation errors."
      ],
      ["• Check the 'Reference Data' sheet for valid Visit Type descriptions."],
      ["• Codes must be unique - duplicates will cause validation errors."],
      [
        "• Override rules must not conflict - two rules that match the same contract/work type for the same SOR code will be rejected."
      ],
      ["• It is recommended to perform imports outside core business hours."]
    ];

    const worksheet = XLSX.utils.aoa_to_sheet(instructions);
    worksheet["!cols"] = [{ wch: 20 }, { wch: 70 }];
    return worksheet;
  }
}
